/**
 * ADMIN-T021 — HTTP client with error categorisation (SPEC §11.6) and limited retry (§8.4).
 *
 * - Adds X-Operation-Id for write methods (POST/PUT/PATCH/DELETE).
 * - Categorises non-2xx responses into ApiError subclasses.
 * - Retries GET on network / 5xx / 429 up to 3 times with 250ms × 2^n backoff (+ jitter).
 * - Triggers `Reauthenticating` on 401.
 */

import { ERROR_CODES, type ErrorCode } from '@/observability/errorCodes';
import { triggerReauth } from '@/app/bootstrap';
import { newOperationId } from './operationId';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  method: HttpMethod;
  url: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  headers?: Record<string, string>;
  baseUrl?: string;
  /** Disable retries for this call (defaults to method-based behaviour). */
  noRetry?: boolean;
  /** Operation id to reuse (idempotency / retry). Generated automatically for writes. */
  operationId?: string;
  /** Bearer token. Injected by the session helper. */
  token?: string;
  signal?: AbortSignal;
}

export interface HttpFetcher {
  (input: string, init: RequestInit): Promise<Response>;
}

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status?: number;
  readonly url: string;
  readonly body?: unknown;
  constructor(code: ErrorCode, message: string, url: string, status?: number, body?: unknown) {
    super(message);
    this.code = code;
    this.url = url;
    this.status = status;
    this.body = body;
    this.name = 'ApiError';
  }
}

const RETRYABLE_STATUSES = new Set([408, 429, 500, 502, 503, 504]);

function buildUrl(opts: RequestOptions, baseUrl: string): string {
  const base = opts.baseUrl ?? baseUrl;
  const path = opts.url.startsWith('http') ? opts.url : `${base.replace(/\/$/, '')}${opts.url}`;
  if (!opts.query) return path;
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(opts.query)) {
    if (v !== undefined && v !== null) usp.append(k, String(v));
  }
  const qs = usp.toString();
  return qs ? `${path}${path.includes('?') ? '&' : '?'}${qs}` : path;
}

function categoriseStatus(status: number): ErrorCode {
  if (status === 401) return ERROR_CODES.auth_expired;
  if (status === 403) return ERROR_CODES.permission_denied;
  if (status === 408) return ERROR_CODES.timeout_error;
  if (status === 409) return ERROR_CODES.conflict;
  if (status === 422) return ERROR_CODES.validation_failed;
  if (status === 429) return ERROR_CODES.rate_limited;
  if (status === 502 || status === 503 || status === 504) return ERROR_CODES.upstream_unavailable;
  if (status >= 500) return ERROR_CODES.internal_error;
  return ERROR_CODES.internal_error;
}

async function readBody(res: Response): Promise<unknown> {
  const ct = res.headers.get('content-type') ?? '';
  try {
    if (ct.includes('application/json')) return await res.json();
    if (ct.startsWith('text/')) return await res.text();
    return null;
  } catch {
    return null;
  }
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    if (signal) {
      const onAbort = () => {
        clearTimeout(t);
        reject(new DOMException('aborted', 'AbortError'));
      };
      if (signal.aborted) onAbort();
      else signal.addEventListener('abort', onAbort, { once: true });
    }
  });
}

export interface HttpClientConfig {
  coreBaseUrl: string;
  biBaseUrl: string;
  fetcher?: HttpFetcher;
  onAuthExpired?: () => void;
  /** Returns the current Bearer token for automatic injection on core requests. */
  getToken?: () => string | null;
}

export class HttpClient {
  private readonly fetcher: HttpFetcher;
  private readonly onAuthExpired: () => void;
  private readonly getToken: (() => string | null) | undefined;
  private coreBaseUrl: string;
  private biBaseUrl: string;

  constructor(config: HttpClientConfig) {
    this.fetcher = config.fetcher ?? ((input, init) => fetch(input, init));
    this.onAuthExpired = config.onAuthExpired ?? (() => triggerReauth());
    this.getToken = config.getToken;
    this.coreBaseUrl = config.coreBaseUrl;
    this.biBaseUrl = config.biBaseUrl;
  }

  setBaseUrls(core: string, bi: string): void {
    this.coreBaseUrl = core;
    this.biBaseUrl = bi;
  }

  resolveBaseUrl(target: 'core' | 'bi' = 'core'): string {
    return target === 'bi' ? this.biBaseUrl : this.coreBaseUrl;
  }

  /**
   * Opens a response without consuming its body. Streaming APIs use this entry
   * point so they share URL resolution, bearer injection, operation ids and
   * authentication-expiry handling with normal JSON requests.
   */
  async requestStream(
    opts: RequestOptions,
    target: 'core' | 'bi' = 'core',
  ): Promise<Response> {
    const isWrite = opts.method !== 'GET';
    const opId = opts.operationId ?? (isWrite ? newOperationId() : undefined);
    const headers: Record<string, string> = {
      Accept: 'text/event-stream',
      ...(opts.headers ?? {}),
    };
    if (opts.body !== undefined && opts.body !== null) {
      headers['Content-Type'] = headers['Content-Type'] ?? 'application/json';
    }
    if (opId) headers['X-Operation-Id'] = opId;
    const bearerToken = opts.token ?? (this.getToken?.() ?? undefined);
    if (bearerToken) headers.Authorization = `Bearer ${bearerToken}`;

    const url = buildUrl(opts, this.resolveBaseUrl(target));
    let response: Response;
    try {
      response = await this.fetcher(url, {
        method: opts.method,
        headers,
        body:
          opts.body === undefined || opts.body === null
            ? undefined
            : typeof opts.body === 'string'
              ? opts.body
              : JSON.stringify(opts.body),
        credentials: 'include',
        signal: opts.signal,
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
      throw new ApiError(
        ERROR_CODES.network_error,
        err instanceof Error ? err.message : String(err),
        url,
      );
    }

    if (response.ok) return response;
    const body = await readBody(response);
    if (response.status === 401) this.onAuthExpired();
    throw new ApiError(
      categoriseStatus(response.status),
      `HTTP ${response.status} ${opts.method} ${opts.url}`,
      url,
      response.status,
      body,
    );
  }

  async request<T>(opts: RequestOptions, target: 'core' | 'bi' = 'core'): Promise<T> {
    const isWrite = opts.method !== 'GET';
    const opId = opts.operationId ?? (isWrite ? newOperationId() : undefined);
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...(opts.headers ?? {}),
    };
    if (opts.body !== undefined && opts.body !== null) {
      headers['Content-Type'] = headers['Content-Type'] ?? 'application/json';
    }
    if (opId) headers['X-Operation-Id'] = opId;
    // Both targets are same-origin Core gateways; BI never receives the browser JWT directly.
    const bearerToken = opts.token ?? (this.getToken?.() ?? undefined);
    if (bearerToken) headers.Authorization = `Bearer ${bearerToken}`;

    const url = buildUrl(opts, this.resolveBaseUrl(target));
    const init: RequestInit = {
      method: opts.method,
      headers,
      body:
        opts.body === undefined || opts.body === null
          ? undefined
          : typeof opts.body === 'string'
            ? opts.body
            : JSON.stringify(opts.body),
      credentials: 'include',
      signal: opts.signal,
    };

    const maxAttempts = opts.noRetry || isWrite ? 1 : 3;
    let attempt = 0;
    // Backoff: 250ms, 500ms, 1000ms — capped (per SPEC §8.4).
    while (true) {
      attempt += 1;
      try {
        const res = await this.fetcher(url, init);
        if (res.ok) {
          if (res.status === 204) return undefined as T;
          return (await readBody(res)) as T;
        }
        const body = await readBody(res);
        const code = categoriseStatus(res.status);

        if (res.status === 401) this.onAuthExpired();

        if (
          !opts.noRetry &&
          !isWrite &&
          RETRYABLE_STATUSES.has(res.status) &&
          attempt < maxAttempts
        ) {
          await sleep(250 * 2 ** (attempt - 1) + Math.random() * 100, opts.signal);
          continue;
        }
        throw new ApiError(code, `HTTP ${res.status} ${opts.method} ${opts.url}`, url, res.status, body);
      } catch (err) {
        if (err instanceof ApiError) throw err;
        if (err instanceof DOMException && err.name === 'AbortError') throw err;
        if (!opts.noRetry && !isWrite && attempt < maxAttempts) {
          await sleep(250 * 2 ** (attempt - 1) + Math.random() * 100, opts.signal);
          continue;
        }
        throw new ApiError(
          ERROR_CODES.network_error,
          err instanceof Error ? err.message : String(err),
          url,
        );
      }
    }
  }

  get<T>(url: string, query?: RequestOptions['query'], target: 'core' | 'bi' = 'core'): Promise<T> {
    return this.request<T>({ method: 'GET', url, ...(query ? { query } : {}) }, target);
  }
}

let _client: HttpClient | null = null;

export function configureHttpClient(config: HttpClientConfig): HttpClient {
  _client = new HttpClient(config);
  return _client;
}

export const httpClient = new Proxy({} as HttpClient, {
  get(_target, prop) {
    if (!_client) {
      // Lazy default — useful for early tests / unit code that doesn't bootstrap.
      _client = new HttpClient({ coreBaseUrl: '/admin-bff', biBaseUrl: '/admin-bff/bi' });
    }
    const value = Reflect.get(_client, prop, _client) as unknown;
    return typeof value === 'function' ? value.bind(_client) : value;
  },
}) as HttpClient;
